import { ThemePrepared } from '@fluentui/react-northstar';
import React, { useContext, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSetState } from '../../AppState';
import { clearFilterStates } from '../../shared/components/Filter';
import { CustomViewType } from '../../shared/utils/constants';
import { CustomViewsDropdown } from '../List/CustomViewsDropdown';
import { CustomViewContext } from './CustomViewContextProvider';
import { addUrlSearchParams, setChildFiltersInStorage, setConfiguredColumnsKeyOrderInStorage, setSelectedCustomView, setSortOrderInStorage, setViewIdsInStorage } from './CustomViewPopup/heplers/customPopupHelper';
import useSelectedViewName from './CustomViewPopup/useSelectedViewName';
import ToolbarCSS from './Toolbar.module.css';

const DropdownTitle = ({ children }) => {
  return (
    <div className='py-1' role='none'>
      <div
        className={`block px-4 py-2 text-sm cursor-default ${ToolbarCSS.parentDropdownTitle}`}
        role='menuitem'
        tabIndex={-1}
        id='menu-item-6'
      >
        {children}
      </div>
    </div>
  );
};
interface PropsI {
  defaultFilters: FilterPropsItems[];
  globalTheme: ThemePrepared<any>;
  isCustomViewEnabled: boolean;
  isScopingEnabled: boolean;
}

export const TableMenu = ({ defaultFilters, globalTheme, isCustomViewEnabled, isScopingEnabled }: PropsI) => {
  const location = useLocation();
  const history = useHistory();

  const setState = useSetState();
  const viewName = useSelectedViewName();
  

  const { customViewStates, setCustomViewStates } = useContext(CustomViewContext);  

  const isMounted = useRef(false);

  const sortedViews = [...(customViewStates?.customViews ?? [])].sort((a, b) => a.Name.localeCompare(b.Name));

  const onClickDefaultView = async (id: number) => {
    // Drop the remembered custom view, otherwise the mount effect below pushes ?id=<old view> back
    // into the url the next time this menu remounts and the system view silently reverts.
    localStorage.removeItem('selectedCustomViewId');
    addUrlSearchParams({ history, location, defaultViewId: id + '' });

    const page = localStorage.getItem('lastTicketPage');

    // Set this to a non-existent view, then set it to the right one via setTimeout to force a refresh
    setState(s => ({ ...s, listFilter: -100, isReload: (page === 'board') }));
    setTimeout(() => setState(s => ({ ...s, listFilter: id, isReload: false })), 0);

    clearFilterStates(setState, defaultFilters);

    setCustomViewStates((prev): CustomViewStatesType => ({
      ...prev,
      view: 'none',
      selectedCustomView: null,
      configureColumnsApplied: {
        prevSortOrder: null,
        sortOrder: null
      },
      configurableColumnsKeyOrder: []
    }));
    
    setConfiguredColumnsKeyOrderInStorage();
    setSortOrderInStorage();
    await setChildFiltersInStorage()
  };

  const onClickCustomView = async (customView: CreateCustomView, defaultView: CreateCustomView) => {
    localStorage.setItem('selectedCustomViewId', customView.Id.toString());
    setViewIdsInStorage({ customViewId: customView.Id.toString(), defaultViewId: defaultView?.Id?.toString() ?? '' })
    setSelectedCustomView({
      defaultFilters,
      customView,
      setCustomViewStates,
      history,
      location,
    });

    setState((s) => ({
      ...s,
      listFilter: Math.random(),
    }));
    clearFilterStates(setState, defaultFilters);

    setConfiguredColumnsKeyOrderInStorage();
    setSortOrderInStorage();
    await setChildFiltersInStorage()
  };

  useEffect(() => {
    isMounted.current = true;

    let savedCustomViewId = localStorage.getItem('selectedCustomViewId');
    if (savedCustomViewId && !location.search.includes(`id=${savedCustomViewId}`)) {
      const isExisting = customViewStates.customViews.some(view => view.Id.toString() === savedCustomViewId);
      if (!isExisting) savedCustomViewId = customViewStates.customViews.find(x=>x.Type===CustomViewType.DefaultView)?.Id.toString();

      const currentPath = location.pathname;
      const newUrl = `${currentPath}?id=${savedCustomViewId}`;
      history.push(newUrl);
    }

  }, []);

  return (
    <CustomViewsDropdown 
      onClickDefaultView={onClickDefaultView}
      showAddView={true}
      sortedViews={sortedViews}
      viewName={viewName}
      globalTheme={globalTheme} 
      isCustomViewEnabled={isCustomViewEnabled} 
      isScopingEnabled={isScopingEnabled}
      selectView={onClickCustomView} 
    />
  );
};
